"use client";

import { type Selection, useSetQuantity } from "@trydig/centra/client";
import type { BaseSelection } from "@trydig/centra-types/selection";
import Image from "next/image";
import { sizes } from "@/components/bloks/Asset";
import { MinusIcon } from "@/components/icons/Minus";
import { PlusIcon } from "@/components/icons/Plus";
import { Skeleton } from "@/components/ui/Skeleton";
import { useIsPreorder } from "@/features/products/hooks";
import {
	ProductPrice,
	ProductPriceSkeleton,
} from "@/features/products/ProductDetailsPage/components/ProductPrice";
import { isProductBundle } from "@/features/products/utils";
import { useTranslations } from "@/i18n/client";
import { Link } from "@/i18n/routing";
import { gtm } from "@/lib/gtm";
import { selectionItemToGTMProduct } from "@/lib/gtm/utils";
import { klaviyoTracker } from "@/lib/klaviyo";
import styles from "./CartItem.module.css";

export type CartItem = NonNullable<
	NonNullable<Selection["selection"]>["items"]
>[number];

export interface CartItemProps {
	item: CartItem;
	isStatic?: boolean;
}

export const CartItem = ({ item, isStatic = false }: CartItemProps) => {
	const setQuantity = useSetQuantity({
		optimistic: true,
	});
	const t = useTranslations();

	const media = item.product?.media?.standard?.[0];

	const isBundle = isProductBundle(item);

	const { data: preorderData } = useIsPreorder({
		centraVariantId: item.product?.centraVariant,
		isBundle,
		displayItemId: item.product?.product || "",
	});

	return (
		<article className={styles.root}>
			<div className={styles.image}>
				<Link style={{ display: "contents" }} href={item.product?.uri ?? "#"}>
					<Image
						sizes={sizes(10)}
						src={media || "/fallback-image.jpg"}
						alt="Vercel"
						fill
					/>
				</Link>
			</div>

			<div className={styles.content}>
				<div className={styles.top}>
					<Link href={item.product?.uri ?? "#"}>
						<p className={styles.title}>{item.product?.name}</p>
						<p className={styles.variantName}>{item.product?.variantName}</p>
					</Link>
					{!isStatic && (
						<button
							type="button"
							onClick={() => {
								setQuantity
									.mutateAsync({
										line: item.line!,
										quantity: 0,
									})
									.then((selection) => {
										const selectionItem = selection?.selection?.items?.at(-1);

										if (selectionItem && selection?.selection?.currency) {
											gtm.removeFromCart(
												selectionItemToGTMProduct({
													item: selectionItem,
													currency: selection.selection.currency,
												}),
											);
										}
									});
							}}
							className={styles.removeButton}
						>
							<PlusIcon className={styles.removeIcon} />
						</button>
					)}
				</div>
				{preorderData && (
					<span className={styles.preorder}>
						{t("Pre-order")} {t("est. delivery")} {preorderData.eta}
					</span>
				)}
				<div className={styles.bottom}>
					<ProductPrice
						className={styles.price}
						discountedPrice={item.product?.price ?? ""}
						price={item.product?.priceBeforeDiscount ?? ""}
					/>
					{!isStatic && (
						<div className={styles.actions}>
							<button
								type="button"
								className={styles.actionBtn}
								onClick={() =>
									setQuantity.mutate(
										{
											line: item.line!,
											quantity: (item.quantity ?? 0) - 1,
										},
										{
											onSuccess: (selection) => {
												const selectionItem =
													selection?.selection?.items?.at(-1);

												if (selectionItem && selection?.selection?.currency) {
													gtm.removeFromCart(
														selectionItemToGTMProduct({
															item: selectionItem,
															currency: selection.selection.currency,
														}),
													);
												}
											},
										},
									)
								}
							>
								<MinusIcon />
							</button>
							<span className={styles.quantity}>{item.quantity}</span>
							<button
								type="button"
								className={styles.actionBtn}
								onClick={() =>
									setQuantity.mutate(
										{
											line: item.line!,
											quantity: (item.quantity ?? 0) + 1,
										},
										{
											onSuccess: (selection, { line }) => {
												const selectionItem = selection?.selection?.items?.find(
													(item) => item.line === line,
												);

												if (selectionItem && selection?.selection?.currency) {
													gtm.addToCart(
														selectionItemToGTMProduct({
															item: selectionItem,
															currency: selection.selection.currency,
														}),
													);
												}

												klaviyoTracker.addToCart(
													selection.selection as BaseSelection,
													item,
												);
											},
										},
									)
								}
							>
								<PlusIcon />
							</button>
						</div>
					)}
				</div>
			</div>
		</article>
	);
};

export const CartItemSkeleton = () => {
	return (
		<article className={styles.root}>
			<Skeleton className={styles.image} />

			<div className={styles.content}>
				<div className={styles.top}>
					<div>
						<Skeleton fitContent>
							<p className={styles.title}>Lorem, ipsum dolor.</p>
						</Skeleton>
						<Skeleton spaced="y" fitContent>
							<p className={styles.variantName}>Lorem, ipsum.</p>
						</Skeleton>
					</div>
					<Skeleton fitContent>
						<button type="button">
							<PlusIcon className={styles.removeIcon} />
						</button>
					</Skeleton>
				</div>
				<Skeleton fitContent>
					<span className={styles.preorder}>Pre-order</span>
				</Skeleton>
				<div className={styles.bottom}>
					<ProductPriceSkeleton />
					<div className={styles.actions}>
						<Skeleton fitContent>
							<button type="button" className={styles.actionBtn}>
								<MinusIcon />
							</button>
						</Skeleton>
						<Skeleton fitContent>
							<span className={styles.quantity}>1</span>
						</Skeleton>
						<Skeleton fitContent>
							<button type="button" className={styles.actionBtn}>
								<PlusIcon />
							</button>
						</Skeleton>
					</div>
				</div>
			</div>
		</article>
	);
};
